import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { AuthSessionService } from '@modules/auth/auth-session.service';
import { NgxSpinnerService } from 'ngx-spinner';
import ResponseCode from '../constants/response.constants';
import { NotificationService } from '../services/notification.service';
import { displayErrors, parseGraphQLErrors } from './functions.helper';
import { OidcSecurityService } from 'angular-auth-oidc-client';

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  private readonly sessionRetryHeader = 'x-session-refresh-retried';

  constructor(
    private notificationService: NotificationService,
    private spinner: NgxSpinnerService,
    private authSession: AuthSessionService,
    private oidc: OidcSecurityService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.spinner.show();

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const body = event.body;

          if (body?.data) {
            const dataKey = Object.keys(body.data)[0];
            const result = body.data?.[dataKey];

            if (result?.errorCode) {
              if (result.errorCode === ResponseCode.SESSION_EXPIRED) {
                return event.clone({
                  body: {
                    ...body,
                    __sessionExpired: true,
                  },
                });
              }

              this.notificationService.errorDialog(result?.message ?? 'Bad Request');
            }
          } else if (body?.errors) {
            const validationErrors = body.errors.filter(
              (err: any) => !err.message?.toLowerCase().includes('access denied')
            );
            if (validationErrors.length) {
              const parsedErrors = parseGraphQLErrors(validationErrors);
              displayErrors(parsedErrors);
            }
          }
        }

        return event;
      }),
      switchMap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.body?.__sessionExpired) {
          return this.handleRefreshToken(request, next, event);
        }

        return of(event);
      }),
      catchError((error: any) => {
        if (Array.isArray(error)) {
          const validationErrors = error.filter(
            (err: any) => !err.message?.toLowerCase().includes('access denied')
          );
          if (validationErrors.length) {
            const parsedErrors = parseGraphQLErrors(validationErrors);
            displayErrors(parsedErrors);
          }
        }

        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handleRefreshToken(request, next, undefined, error);
        }

        if (error instanceof HttpErrorResponse) {
          this.notificationService.errorMessage('Server or Network error!');
          return throwError(() => error);
        }

        this.notificationService.errorMessage('Server or Network error!');
        return throwError(() => error);
      }),
      finalize(() => this.spinner.hide()),
    );
  }

  private handleRefreshToken(
    request: HttpRequest<any>,
    next: HttpHandler,
    fallbackEvent?: HttpEvent<any>,
    fallbackError?: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    if (request.headers.has(this.sessionRetryHeader)) {
      return fallbackError ? throwError(() => fallbackError) : of(fallbackEvent as HttpEvent<any>);
    }

    return this.authSession.refreshSession().pipe(
      switchMap((success) => {
        if (!success) {
          return fallbackError
            ? throwError(() => fallbackError)
            : of(fallbackEvent as HttpEvent<any>);
        }

        return this.oidc.getAccessToken().pipe(
          take(1),
          switchMap((newToken) => {
            const retriedRequest = request.clone({
              setHeaders: {
                [this.sessionRetryHeader]: 'true',
                Authorization: `Bearer ${newToken}`,
              },
            });
            return next.handle(retriedRequest);
          })
        );
      }),
    );
  }
}
